import React from 'react'
import { useUpdateLog } from 'utils/log'
import { OptionItem } from '../common'
import CheckLine from '../items/CheckLine'
import { FormTitleProps } from '../items/FormTitle'
import BaseGroup from './BaseGroup'

/**
 * CheckBoxGroupのタイプ定義
 */
export type CheckItemGroupProps<T> = FormTitleProps & {
  /** チェックボックスの選択肢一覧 */
  options: Array<string | OptionItem<T>>
  /** 選択中のインデックスのリスト */
  selectedIndexies?: number[]
  /** 子要素に値を割り振っている場合に選択中の値のリスト */
  selectedValues?: T[]
  /** チェックの排他指定 */
  isExclusion?: boolean
  /** 選択肢に値が設定されている場合の変更時コールバック */
  onChanged?: (indexies: number[]) => void
  /** 選択肢に値が設定されている場合の変更時コールバック */
  onValueChanged?: (values: T[]) => void
}

/**
 * チェックグループ
 */
const CheckBoxGroup: <T>(
  props: CheckItemGroupProps<T>
) => React.ReactElement = <T,>(props: CheckItemGroupProps<T>) => {
  const {
    title,
    description,
    isRequired,
    hideTitle,
    options,
    selectedIndexies,
    selectedValues,
    isExclusion,
    onChanged,
    onValueChanged,
  } = props

  /** チェックボックスクリック時のコールバック */
  const handleCheckClick = (index: number, value?: T): void => {
    if (onChanged && selectedIndexies) {
      /** onChangedに渡すリスト */
      let indexies: number[]
      if (selectedIndexies.some((x) => x === index)) {
        indexies = selectedIndexies.filter((x) => x !== index)
      } else {
        indexies = isExclusion ? [index] : selectedIndexies.concat(index)
      }
      onChanged(indexies)
    }
    if (onValueChanged && value && selectedValues) {
      /** onValueChangedに渡すリスト */
      let values: T[]
      if (selectedValues.some((x) => x === value)) {
        values = selectedValues.filter((x) => x !== value)
      } else {
        values = isExclusion ? [value] : selectedValues.concat(value)
      }
      onValueChanged(values)
    }
  }

  useUpdateLog(
    title,
    {
      title: title,
      isRequired: isRequired,
      isExclusion: isExclusion,
      selectedValues: selectedValues,
    },
    [props]
  )

  // 子要素作成
  const items: () => React.ReactElement = () => (
    <>
      {options.map((option, index) => {
        const [label, value] =
          typeof option === 'string'
            ? [option, undefined]
            : [option.label, option.value as T]
        const isSelected = selectedValues
          ? selectedValues.some((x) => x === value)
          : selectedIndexies
          ? selectedIndexies.some((x) => x === index)
          : false
        return (
          <CheckLine
            key={index}
            label={label}
            isChecked={isSelected}
            onClick={(): void => handleCheckClick(index, value)}
          />
        )
      })}
    </>
  )

  return (
    <>
      {hideTitle ? (
        items()
      ) : (
        <BaseGroup
          isRequired={isRequired}
          title={title}
          description={description}
        >
          {items()}
        </BaseGroup>
      )}
    </>
  )
}

export default React.memo(CheckBoxGroup) as typeof CheckBoxGroup
